/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import FinanceDocumentScanner from "../finance/FinanceDocumentScanner";
import DocumentScanVersions from "../finance/DocumentScanVersions";
import { appendScannedOriginals } from "../../lib/documentScanner";
import { reviewedPaymentFields } from "./paymentScanFields";
import { Paperclip, FileText, X, PencilLine, Trash2, Loader2, Upload } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { fmtDT } from "../../lib/date";
import apiClient, { authStore } from "../../lib/api";
import AttachmentLightbox from "../AttachmentLightbox";

/* 合同付款期明细 · 「附件」列 + 附件列表弹窗（2026-10-13 新增）
   ─────────────────────────────────────────────────────────────
   需求来源：已签约客户详情（/crm/signed/LEADxxx）→「合同付款期明细」表新增「附件」列，需要：
     ① 回显每期 / 每变更单已上传的支付凭证（数量徽章 → 弹窗列表明细）；
     ② 修改已上传的凭证 —— 可改付款人 / 金额 / 备注，也可重新选一个文件替换旧的；
     ③ 删除凭证（二次确认）。
   覆盖范围：合同各期 + 同表下方的「设计变更（变更单）」行，两处行为一致。

   数据来源：后端 /crm/signed/{id} 的 payments[].proofs 与 changeOrderPayments[].proofs；
   增删改走后端 PATCH / DELETE `.../proof/{pid}`；已签客户页的上传、修改、删除分别读取服务端动作权限，
   销售只可查看，财务核对过的凭证修改仍须保留审计日志且不改变核销状态。

   实现取舍（为什么这么写）：
   ① 列表弹窗用**纯 fixed overlay**（z-[9988]）而不是项目常用的 Radix Dialog：
      Radix 模态弹窗会给 document.body 设 `pointer-events: none`，只对自己的 Content 重新放行；
      而全屏预览灯箱（AttachmentLightbox）是渲染在 body 下的独立 overlay，会被一起禁用而点不动。
      纯 overlay 不存在该问题，且与 AttachmentLightbox 同一套 z-index 体系，可安全叠加。
   ② 预览直接复用现成的 AttachmentLightbox：图片 / PDF 页内打开，带下载、左右翻页、键盘与触摸切换。
   ③ 修改统一按 multipart 提交（payerName / amount / note + 可选 file），后端一个接口同时支持「只改信息」与「换文件」。
   ④ 已核销凭证允许修改：按用户口径只改内容、**不改核销状态**（不重置为待核销、不重新通知财务），
      避免一条已入账凭证反复出现在待核销队列；是否需要重新核销由财务在队列里自行处理。
   ⑤ 列表行改为**左侧缩略图 + 点击即放大**（2026-10-14 改）：
      修改原因 —— 原实现每行只给一个通用文件图标 + 文件名，要看内容必须先点「预览」按钮（或文件名）
      才弹出灯箱，等于「打开弹窗 → 找到文件 → 再点一次 → 才看到图」三步；而付款凭证绝大多数是
      客户手机拍的图，肉眼扫一眼就能确认，多这一跳纯属绕路，用户反馈「操作流程过长」。
      最终效果 —— 每行左侧直接渲染真实缩略图，**点缩略图一次即放大**；原「预览」按钮移除，流程从三步压到两步。
      关键取舍 —— 缩略图不引入任何前端 PDF 库，直接复用后端既有的 `GET /files/{fid}/thumb`
      （见 backend/server.py `_ensure_thumb`：图片原样返回、PDF 用 pypdfium2 渲染首页 PNG 并缓存到
      files.thumbFileId、其他类型 404），因此前端零新增依赖、零构建链改动，且缩略图带 24h 缓存。
      加载失败时（如 HEIC、非图片类型）降级回文件图标，保证行内信息不缺失；灯箱里打开的仍是原图。 */

// 凭证状态 → 中文 / 英文 / 徽章配色（与付款页、待核销队列口径一致）
const ST = {
  pending: ["待核销", "Pending", "bg-amber-100 text-amber-800"],
  confirmed: ["已核销", "Confirmed", "bg-emerald-100 text-emerald-700"],
  rejected: ["未到账 · 已驳回", "Rejected", "bg-red-100 text-red-700"],
};
// 扩展名 → MIME：AttachmentLightbox 依赖 contentType 判断图片 / PDF，凭证记录只带 ext，故在此还原
const CT = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", heic: "image/heic", heif: "image/heif", gif: "image/gif" };
const ctOf = (p) => CT[String(p?.ext || p?.name || "").split(".").pop().toLowerCase()] || "";
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-300";
const PAYMENT_METHOD_LABELS = { check: "Check", zelle: "Zelle", wire: "Wire", cash: "Cash" };

/* 收款计划附件列直接显示首张凭证缩略图；查看时从图直接进入灯箱，省去先开列表再选文件的一步。
   pending=true 时保留待核销提示色；多份附件在灯箱中可继续切换，缩略图加载失败则退回附件图标。 */
export function ProofsCell({ api, proofs, zh, pending, onOpen, tid }) {
  const [thumbError, setThumbError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const n = (proofs || []).length;
  if (!n) return <span className="text-slate-300" data-testid={tid} data-count={0}>—</span>;
  const viewable = (proofs || []).filter((proof) => proof.fileId);
  const items = viewable.map((proof) => ({
    fileId: proof.fileId, filename: proof.name || "proof", contentType: ctOf(proof),
  }));
  const auth = encodeURIComponent(authStore.get("ali_token") || "");
  const client = api || apiClient;
  const baseUrl = client?.defaults?.baseURL || "";
  const first = viewable[0];
  const thumbUrl = first ? `${baseUrl}/files/${encodeURIComponent(first.fileId)}/thumb?auth=${auth}` : "";
  const fileUrl = (fid) => `${baseUrl}/files/${encodeURIComponent(fid)}/download?auth=${auth}`;
  const canPreview = !!items.length;
  return (
    <>
      <button
        type="button"
        onClick={() => canPreview ? setLightboxIndex(0) : onOpen?.()}
        data-testid={tid}
        data-count={n}
        data-pending={pending ? 1 : 0}
        title={zh ? `点击放大查看 ${n} 份付款凭证` : `Click to enlarge ${n} payment proof(s)`}
        aria-label={zh ? `放大查看 ${n} 份付款凭证` : `Enlarge ${n} payment proof(s)`}
        className={"relative inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " + (pending ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-slate-200 bg-slate-50 text-slate-600 hover:border-brand-300 hover:text-brand-600") + (canPreview ? " cursor-zoom-in" : "")}
      >
        {thumbUrl && !thumbError ? <img src={thumbUrl} alt="" className="h-full w-full object-cover" onError={() => setThumbError(true)} /> : <Paperclip aria-hidden="true" className="h-4 w-4" />}
        {n > 1 && <span className="absolute bottom-0.5 right-0.5 rounded bg-slate-900/75 px-1 text-[9px] leading-4 text-white">{n}</span>}
      </button>
      {lightboxIndex !== null && canPreview && <AttachmentLightbox api={client} items={items} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndex={setLightboxIndex} urlOf={fileUrl} zh={zh} />}
    </>
  );
}

/* 附件列表弹窗：
   · 列表视图：该付款期 / 变更单的全部付款凭证（缩略图 / 文件名 / 时间 / 上传人 / 付款人 / 金额 / 状态）；
     点缩略图或文件名 → 叠加 AttachmentLightbox 页内预览原图；canEdit / canDelete 分别控制修改与删除。
   · 修改视图：付款人 / 金额 / 备注 + 可选换文件 → PATCH 提交。
   CRM target 为 { kind: "pay", leadId, key } / { kind: "co", leadId, coId }；
   项目 target 为 { kind: "project-payment", projectId, stage } / { kind: "project-co", projectId, coId }，仅开放删除。 */
export function PayProofsDialog({ api, proofs, title, zh, onClose, onChanged, canEdit, canDelete = canEdit, target }) {
  const list = proofs || [];
  const crmSignedTarget = target?.kind === "pay" || target?.kind === "co";
  const [idx, setIdx] = useState(null);            // 当前预览的附件下标；null = 只看列表
  const [editing, setEditing] = useState(null);    // 正在修改的凭证 pid；null = 不在编辑
  const [form, setForm] = useState({ payee: "", payerName: "", paymentMethod: "", amount: "", note: "", file: null });
  const [payerChoice, setPayerChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanFiles, setScanFiles] = useState([]);
  // 缩略图加载失败的 fileId 集合：失败时该行降级为通用文件图标，避免出现破图占位
  const [thumbErr, setThumbErr] = useState({});
  // ESC 关闭弹窗：预览灯箱打开时把 ESC 让给灯箱（灯箱自己处理关闭）；编辑态下先退出编辑
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (idx !== null || scanFiles.length) return;
      if (editing) { setEditing(null); return; }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, editing, onClose, scanFiles.length]);
  // 原图地址（灯箱用）：/files/{fid}/download
  const fileUrl = (fid) => `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
  // 缩略图地址（列表用）：复用后端既有 /files/{fid}/thumb —— 图片原样返回、PDF 返回首页 PNG，
  // 后端自带 24h 缓存，所以列表里直接当普通 <img> 用即可，无需前端再做 PDF 渲染。
  const thumbUrl = (fid) => `${api.defaults.baseURL}/files/${fid}/thumb?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
  const items = list.map((p) => ({ fileId: p.fileId, filename: p.name || "proof", contentType: ctOf(p) }));
  // 增删改接口地址：合同期 → /payments/{key}/proof/{pid}；变更单 → /change-orders/{coid}/proof/{pid}
  const proofUrl = (pid) => {
    const id = encodeURIComponent(pid);
    if (target?.kind === "project-payment") return `/finance/project/${encodeURIComponent(target.projectId)}/payments/${encodeURIComponent(target.stage)}/proof/${id}`;
    if (target?.kind === "project-co") return `/finance/project/${encodeURIComponent(target.projectId)}/change-orders/${encodeURIComponent(target.coId)}/proof/${id}`;
    return target?.kind === "co"
      ? `/crm/signed/${encodeURIComponent(target.leadId)}/change-orders/${encodeURIComponent(target.coId)}/proof/${id}`
      : `/crm/signed/${encodeURIComponent(target?.leadId)}/payments/${encodeURIComponent(target?.key)}/proof/${id}`;
  };
  const cur = editing ? list.find((p) => p.pid === editing) : null;

  const startEdit = (p) => {
    setEditing(p.pid);
    const payerName = p.payerName || "";
    setPayerChoice(!payerName ? "" : payerName.trim().toUpperCase() === "OWNER" ? "owner" : "other");
    setForm({ payee: p.payee || "", payerName, paymentMethod: p.paymentMethod || "", amount: p.amount != null ? String(p.amount) : "", note: p.note || "", file: null });
  };
  const save = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payee", form.payee || "");
      fd.append("payerName", form.payerName || "");
      fd.append("amount", form.amount || "");
      fd.append("note", form.note || "");
      if (crmSignedTarget) fd.append("paymentMethod", form.paymentMethod || "");
      if (form.file) { fd.append("file", form.file); appendScannedOriginals(fd, form.file); }
      await api.patch(proofUrl(cur.pid), fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(zh ? "凭证已更新" : "Proof updated");
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const prepared = ({ scanFile, originals, recognition }) => {
    if (!scanFile && originals.length !== 1) { toast.error(zh ? "请将多张照片合成为一份 PDF。" : "Combine the photos into one PDF."); return; }
    // 更换凭证不覆盖已录金额；经核对的识别建议也仅能填空。
    const fields = { ...reviewedPaymentFields(recognition) };
    const recognizedMethod = String(fields.paymentMethod || "").trim().toLowerCase();
    if (crmSignedTarget && ["check", "zelle", "wire", "cash"].includes(recognizedMethod)) fields.paymentMethod = recognizedMethod;
    else delete fields.paymentMethod;
    const recognizedPayer = String(fields.payerName || "").trim();
    if (crmSignedTarget && recognizedPayer.toUpperCase() === "OWNER") fields.payerName = "OWNER";
    if (crmSignedTarget && !String(form.payerName || "").trim() && recognizedPayer) {
      setPayerChoice(recognizedPayer.toUpperCase() === "OWNER" ? "owner" : "other");
    }
    setForm(prev => {
      const next = { ...prev, file: scanFile || originals[0] };
      if (recognition?.applyFields) [...(crmSignedTarget ? ["payee", "payerName", "paymentMethod"] : ["payee", "payerName"]), "amount", "note"].forEach(key => {
        if (!String(prev[key] ?? "").trim() && fields[key] != null) next[key] = String(fields[key]);
      });
      return next;
    });
    setScanFiles([]);
  };
  const remove = async (p) => {
    if (busy || !canDelete || !target) return;
    if (!window.confirm(zh ? `删除错误附件「${p.name || p.pid}」？收款金额、核销状态及历史记录保留，删除后可重新上传正确附件。` : `Remove the incorrect attachment "${p.name || p.pid}"? Received amounts, reconciliation status and history stay unchanged. You can upload the correct attachment afterward.`)) return;
    setBusy(true);
    try {
      await api.delete(proofUrl(p.pid));
      toast.success(zh ? "凭证已删除" : "Proof deleted");
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-[9988] flex items-center justify-center p-4" data-testid="pay-proofs-dialog">
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-lg max-h-[82dvh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
            <Paperclip className="h-4 w-4 text-brand-500" />
            <p className="text-sm font-semibold text-slate-800 truncate" data-testid="pay-proofs-title">{editing ? (zh ? `修改凭证 · ${cur?.name || ""}` : `Edit · ${cur?.name || ""}`) : title}</p>
            {!editing && <span className="text-[11px] px-1.5 rounded-full bg-slate-100 text-slate-600" data-testid="pay-proofs-count">{list.length}</span>}
            <button type="button" onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-slate-400 hover:text-slate-700" data-testid="pay-proofs-close" title={zh ? "关闭" : "Close"}><X className="h-4 w-4" /></button>
          </div>

          {editing ? (
            /* ---- 修改凭证视图：收款人 / 付款人 / 支付方式及金额可修正，保证上传后仍能更正录入错误。 ---- */
            <div className="p-4 space-y-3" data-testid="pay-proof-edit">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-500">{zh ? "付款金额 $" : "Amount $"}</span>
                  <input type="number" min="0" step="0.01" className={inp} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-proof-edit-amount" /></label>
                <label className="block"><span className="text-xs text-slate-500">{crmSignedTarget ? (zh ? "收款人" : "Recipient") : (zh ? "公司 / 收款方" : "Company / payee")}</span>
                  <input className={inp} value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} data-testid="pay-proof-edit-payee" /></label>
                {crmSignedTarget ? <div className="block">
                  <label><span className="text-xs text-slate-500">{zh ? "付款人" : "Payer"}</span>
                    <select className={inp} value={payerChoice} onChange={(e) => {
                      const choice = e.target.value;
                      setPayerChoice(choice);
                      setForm((prev) => ({ ...prev, payerName: choice === "owner" ? "OWNER" : "" }));
                    }} data-testid="pay-proof-edit-payer-choice">
                      <option value="">{zh ? "请选择" : "Select"}</option><option value="owner">OWNER</option><option value="other">{zh ? "其他（手动输入）" : "Other (enter manually)"}</option>
                    </select>
                  </label>
                  {payerChoice === "other" && <input className={inp} value={form.payerName} onChange={(e) => setForm({ ...form, payerName: e.target.value })} placeholder={zh ? "请输入付款人姓名" : "Enter payer name"} aria-label={zh ? "手动输入付款人" : "Enter payer name"} data-testid="pay-proof-edit-payer" />}
                </div> : <label className="block"><span className="text-xs text-slate-500">{zh ? "付款人" : "Payer"}</span>
                  <input className={inp} value={form.payerName} onChange={(e) => setForm({ ...form, payerName: e.target.value })} data-testid="pay-proof-edit-payer" /></label>}
                {crmSignedTarget && <label className="block"><span className="text-xs text-slate-500">{zh ? "支付方式" : "Payment method"}</span>
                  <select className={inp} value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} data-testid="pay-proof-edit-payment-method">
                    <option value="">{zh ? "请选择" : "Select"}</option><option value="check">Check</option><option value="zelle">Zelle</option><option value="wire">Wire</option><option value="cash">Cash</option>
                  </select>
                </label>}
              </div>
              <label className="block"><span className="text-xs text-slate-500">{zh ? "备注（支票号 / 转账渠道 / 日期）" : "Note (check no. / channel / date)"}</span>
                <input className={inp} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pay-proof-edit-note" /></label>
              <label className="block">
                <span className="text-xs text-slate-500">{zh ? "更换文件（可选；不选则保留原文件）" : "Replace file (optional)"}</span>
                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,image/*,application/pdf" onChange={(e) => { setScanFiles(Array.from(e.target.files || [])); e.target.value = ""; }} className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100" data-testid="pay-proof-edit-file" />
                <span className="mt-1 block text-[10.5px] text-slate-400" data-testid="pay-proof-edit-current">{zh ? "当前文件" : "Current"}: {cur?.name || "—"}{form.file ? ` → ${form.file.name}` : ""}</span>
                {form.file?.documentScan && <span className="mt-1 block text-xs text-slate-500">{zh ? "扫描件及原件：" : "Scan and originals: "}{form.file.documentScan.originals.map(f => f.name).join("、")}</span>}
              </label>
              <p className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">{zh ? "若该凭证已被财务核销：修改只更新附件内容、不改核销状态，并记录审计日志。" : "If already confirmed: editing updates the attachment only, the reconciliation status is unchanged, and an audit log is recorded."}</p>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" disabled={busy} onClick={() => setEditing(null)} data-testid="pay-proof-edit-cancel">{zh ? "返回" : "Back"}</Button>
                <Button size="sm" disabled={busy} onClick={save} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="pay-proof-edit-save">
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存" : "Save")}
                </Button>
              </div>
            </div>
          ) : (
            /* ---- 列表视图 ---- */
            <>
              <ul className="divide-y divide-slate-100" data-testid="pay-proofs-list">
                {list.map((p, i) => {
                  const [zhl, enl, tone] = ST[p.status] || ST.pending;
                  const canView = !!p.fileId;
                  const isPdf = ctOf(p) === "application/pdf";
                  return (
                    <li key={p.pid || p.fileId || i} className="px-4 py-3 flex items-start gap-3" data-testid={`pay-proof-row-${p.pid || i}`} data-status={p.status}>
                      {/* 缩略图（列表主入口）：点一次直接进灯箱看原图，不再需要先点「预览」。
                          图片与 PDF 都走 /thumb（PDF 由后端渲染首页），失败则降级为文件图标。
                          ⚠️ 刻意**不加 `loading="lazy"`**：本弹窗是 fixed overlay，Chrome 对 fixed 定位元素做懒加载判定时
                          会参考它在文档流中的位置——本弹窗挂在长页面靠后处，实测被判为「不在视口」而始终不发起加载，
                          结果是缩略图一直空白（naturalWidth 恒为 0，且不触发 onError，连降级都救不回来）。
                          这里凭证条数少、后端缩略图本身也只有 640px，直接即时加载，行为可预期。 */}
                      <button
                        type="button"
                        disabled={!canView}
                        onClick={() => canView && setIdx(i)}
                        data-testid={`pay-proof-thumb-${p.pid || i}`}
                        title={canView ? (zh ? "点击放大查看" : "Click to enlarge") : p.name}
                        className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`h-12 w-12 shrink-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center ${canView ? "cursor-zoom-in hover:border-brand-300 hover:ring-2 hover:ring-brand-100 transition-shadow" : "cursor-default"}`)}
                      >
                        {canView && !thumbErr[p.fileId] ? (
                          <img
                            src={thumbUrl(p.fileId)}
                            alt={p.name || ""}
                            className={`h-full w-full object-cover ${isPdf ? "object-top bg-white" : ""}`}
                            onError={() => setThumbErr((m) => ({ ...m, [p.fileId]: true }))}
                          />
                        ) : (
                          <FileText className="h-5 w-5 text-slate-400" />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          disabled={!canView}
                          onClick={() => setIdx(i)}
                          className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-left text-xs font-medium max-w-full truncate ${canView ? "text-brand-700 hover:underline" : "text-slate-500 cursor-default"}`)}
                          title={p.name}
                        >
                          {p.name || (zh ? "未命名" : "unnamed")}
                        </button>
                        <p className="text-[10.5px] text-slate-400 mt-0.5">
                          {p.at ? fmtDT(p.at) : ""}
                          {p.uploadedBy ? ` · ${p.uploadedBy}` : ""}
                          {p.payee ? ` · ${zh ? (crmSignedTarget ? "收款人" : "公司") : (crmSignedTarget ? "recipient" : "company")} ${p.payee}` : ""}
                          {p.payerName ? ` · ${zh ? "付款人" : "payer"} ${p.payerName}` : ""}
                          {p.paymentMethod ? ` · ${zh ? "支付方式" : "method"} ${PAYMENT_METHOD_LABELS[String(p.paymentMethod).toLowerCase()] || p.paymentMethod}` : ""}
                          {p.amount ? ` · ${money(p.amount)}` : ""}
                        </p>
                        {p.note ? <p className="text-[10.5px] text-slate-400 mt-0.5">{p.note}</p> : null}
                        {canView && p.documentScan && <DocumentScanVersions api={api} zh={zh} fileId={p.fileId} />}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0 ${tone}`} data-testid={`pay-proof-status-${p.pid || i}`}>{zh ? zhl : enl}</span>
                      <div className="flex-wrap md:flex-nowrap flex items-center gap-1 shrink-0">
                        {canEdit && target && (
                          <button type="button" disabled={busy} onClick={() => startEdit(p)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-500 hover:text-brand-600 inline-flex items-center gap-0.5" data-testid={`pay-proof-edit-${p.pid || i}`} title={zh ? "修改凭证（信息 / 换文件）" : "Edit proof"}>
                            <PencilLine className="h-3.5 w-3.5" />{zh ? "修改" : "Edit"}
                          </button>
                        )}
                        {canDelete && target && (
                          /* 财务页新增“管理附件”入口后，要让误传文件的移除动作在列表中可见，不能只靠图标悬停提示。 */
                          <button type="button" disabled={busy} onClick={() => remove(p)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-destructive hover:text-red-700 inline-flex items-center gap-0.5" data-testid={`pay-proof-delete-${p.pid || i}`} title={zh ? "删除错误附件" : "Remove incorrect attachment"} aria-label={zh ? `删除错误附件：${p.name || "未命名"}` : `Remove incorrect attachment: ${p.name || "unnamed"}`}>
                            <Trash2 className="h-3.5 w-3.5" />{zh ? "删除" : "Remove"}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="px-4 py-2 text-[10.5px] text-slate-400 border-t border-slate-100">{zh ? "点击缩略图或文件名即可放大查看（图片 / PDF）；文件由客户在付款页或财务上传。" : "Click the thumbnail or file name to enlarge (image / PDF); uploaded by the customer or Finance."}</p>
            </>
          )}
        </div>
      </div>
      {idx != null && items[idx] && <AttachmentLightbox api={api} items={items} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} urlOf={fileUrl} zh={zh} />}
      <FinanceDocumentScanner api={api} zh={zh} files={scanFiles} maxOriginalFiles={1} maxBytes={20 * 1024 * 1024} onClose={() => setScanFiles([])} onPrepared={prepared} />
    </>
  );
}
